/* global Word */
/**
 * Validador do documento — o "linter" da padronização Norven.
 *
 * DETERMINÍSTICO, SEM LLM: cada checagem é uma comparação objetiva contra os
 * contratos dos rules.json (`_base` + tipo selecionado) — fonte, tamanho, cor,
 * alinhamento, numeração de títulos, tabelas e paleta das imagens (esta via
 * `POST /validate/image`, também sem LLM). Mesmo documento ⇒ mesmo resultado,
 * custo zero em tokens.
 *
 * ESCOPO: a FORMA do documento, nunca o seu conteúdo. Fonte, tamanho, cor,
 * negrito, alinhamento, entrelinha, respiro entre parágrafos, numeração dos
 * títulos, marcador de lista, tabela e paleta das imagens — o tipo de checagem
 * que um revisor faria com a régua da norma na mão, sem ler o texto.
 *
 * O que ficou DELIBERADAMENTE de fora: tudo que depende do sentido do
 * documento. O validador não cobra seção obrigatória do tipo, não confere se a
 * fronteira do escopo tem a frase-trava, não julga se cada exclusão nomeia um
 * responsável. Essas regras existem e valem — mas quem as aplica é o prompt (o
 * manual do tipo vai inteiro no contexto) e a revisão humana. Um badge que
 * mistura "a fonte está errada" com "falta a seção Equipe" mede duas coisas
 * diferentes na mesma nota e o usuário perde a que dá para agir na hora.
 *
 * DUAS PASSADAS, escopos diferentes:
 *  - `checkParagraphs` — um parágrafo por vez: fonte, tamanho, cor, negrito,
 *    alinhamento, entrelinha, respiro, numeração, marcador digitado à mão.
 *  - `checkStructure` — o documento inteiro: sequência da numeração, título
 *    vazio, linha em branco entre conteúdos, espaço depois de tabela e
 *    pontuação dos itens de lista.
 *
 * Todos os VALORES vêm do rules.json (`style_ref_contract`, `color_policy`,
 * `table_contract`, `list_contract`, `numbering_contract`) — o validador não
 * guarda régua própria, senão viraria mais um lugar para esquecer de atualizar
 * quando a norma mudar.
 *
 * Roda ao final de cada turno que ESCREVEU no documento (ver taskpane.ts) e
 * alimenta o badge de validação no header. Só valida e informa — nunca altera.
 *
 * Granularidade de RUN (não de parágrafo). O Office.js devolve `font.name`/
 * `size`/`color` do parágrafo apenas quando são UNIFORMES; se houver mistura
 * (uma letra em outra fonte, o ¶ do fim mantendo a fonte antiga ao trocar só o
 * título, etc.) devolve null. Ler só no nível do parágrafo faz essas mudanças
 * passarem despercebidas. Por isso: quando o parágrafo é uniforme, checamos o
 * valor direto (rápido); quando é MISTO, quebramos em runs (getTextRanges por
 * palavra sobre o conteúdo, sem o ¶) e checamos cada trecho — pegando fonte,
 * tamanho e cor no detalhe, inclusive de uma única letra.
 *
 * Princípio anti-falso-positivo: um valor UNIFORME concreto é verdade; null só
 * aparece em trecho realmente misto. Host sem suporte ⇒ checagem pulada, não
 * reprovada. Ser criterioso ≠ chutar.
 *
 * Capa: checagem LENIENTE de marca (nunca cega, nunca de corpo). A diagramação
 * da capa vem do template.docx (`protected_regions.cover`) e cada tipo a
 * implementa com estilos Word diferentes — julgá-la pelas regras de corpo
 * gerou falso positivo real ("do" do placeholder em Styrene 24pt roxo acusado
 * como corpo fora do padrão), e PULAR a zona cegamente escondeu violações
 * reais (Arial 28 no documento inteiro passando batido, porque o fallback da
 * fronteira marcava 40 parágrafos como "capa"). O equilíbrio: na zona da capa
 * exigimos apenas fonte DA MARCA (Styrene A/Muli/Courier) e cor DA PALETA, sem
 * julgar tamanho; headings de verdade NUNCA são pulados (validação completa
 * onde estiverem); e a fronteira da capa é apertada (page break, Sumário ou o
 * 1º heading — numerado ou não; fallback de só 12 parágrafos).
 */
import { validateImage } from '../api/client';
import { readRulesJson } from './template';
import { dlog } from '../util/log';
// Limites de varredura: acima disso o documento é truncado (reportado no
// detalhe) para a validação nunca travar a UI em docs gigantes.
const MAX_PARAGRAPHS = 800;
const MAX_IMAGES = 8;
// Quantos parágrafos MISTOS aprofundamos em runs por rodada. Só parágrafos
// com formatação não-uniforme entram aqui (documento limpo = 0), então o teto
// existe só para blindar um doc patológico (tudo misto) de travar a UI.
const MAX_MIXED_DRILL = 160;
// Ranges (palavras) por parágrafo misto — evita estourar em parágrafos enormes.
const MAX_RUNS_PER_PARAGRAPH = 120;
/** Lê um caminho aninhado de um objeto JSON sem explodir. */
function pick(obj, ...path) {
    let cur = obj;
    for (const key of path) {
        if (!cur || typeof cur !== 'object')
            return undefined;
        cur = cur[key];
    }
    return cur;
}
function asString(v, fallback) {
    return typeof v === 'string' && v ? v : fallback;
}
function asNumber(v, fallback) {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
/**
 * Número do rules.json, ou `null` quando a norma NÃO fixa aquele valor.
 * Distinto de `asNumber`: aqui a ausência é informação — sem valor na norma,
 * não há o que cobrar, e inventar um default viraria falso positivo.
 */
function numOrNull(v) {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
/** Monta os contratos checáveis a partir dos rules.json (base + tipo). */
function buildContracts(base, type) {
    var _a, _b;
    const styleRef = (name) => pick(base, 'style_ref_contract', name);
    // Defaults por NÍVEL (usados só se o rules.json não trouxer o valor): a
    // hierarquia do template diferencia tamanho e cor por nível — 16 roxo, 12
    // roxo, 11 cinza, 11 lilás sem negrito. Um default único (14pt roxo) fazia o
    // validador cobrar do H3/H4 uma cor que o template não usa.
    const HEADING_DEFAULTS = {
        1: { font: 'Styrene A Bold', size: 16, color: '#550899' },
        2: { font: 'Styrene A Bold', size: 12, color: '#550899' },
        3: { font: 'Styrene A Bold', size: 11, color: '#3C3C3C' },
        4: { font: 'Styrene A Regular', size: 11, color: '#AC4DFF' },
    };
    const COLOR_NAMES = {
        '#550899': 'o roxo Norven',
        '#3C3C3C': 'o cinza escuro',
        '#AC4DFF': 'o lilás Norven',
        '#1B1B1B': 'o quase-preto',
    };
    const heading = (n) => {
        var _a;
        const ref = styleRef(`heading_${n}`);
        const def = HEADING_DEFAULTS[n];
        // "1. Título" / "1.1 Sub" / "1.1.1 ..." — aceita com ou sem ponto final.
        const pattern = new RegExp(`^\\d+(?:\\.\\d+){${n - 1}}\\.?\\s+\\S`);
        const color = asString(pick(ref, 'font_color'), def.color).toUpperCase();
        const bold = pick(ref, 'bold');
        return {
            font: asString(pick(ref, 'font_family'), def.font),
            size: asNumber(pick(ref, 'font_size_pt'), def.size),
            color,
            bold: typeof bold === 'boolean' ? bold : null,
            spaceBefore: numOrNull(pick(ref, 'space_before_pt')),
            spaceAfter: numOrNull(pick(ref, 'space_after_pt')),
            pattern,
            label: n === 1 ? 'tópico principal' : `subtópico de nível ${n}`,
            colorName: (_a = COLOR_NAMES[color]) !== null && _a !== void 0 ? _a : 'a cor do padrão',
        };
    };
    const bodyRef = styleRef('body_paragraph');
    const colorPolicy = pick(base, 'color_policy');
    const bodyAllowed = (_a = pick(colorPolicy, 'body_allowed')) !== null && _a !== void 0 ? _a : [
        '#1B1B1B', '#000000', '#3C3C3C',
    ];
    const forbidden = (_b = pick(colorPolicy, 'forbidden_body_colors')) !== null && _b !== void 0 ? _b : [
        '#AC4DFF', '#518E16', '#AAF766', '#A34200', '#FF6A00',
    ];
    const typeId = asString(pick(type, 'id'), '');
    const matrixEntry = pick(base, 'document_variant_contract', 'known_type_matrix', typeId);
    const typeCover = pick(type, 'document_structure', 'has_cover');
    const matrixCover = pick(matrixEntry, 'has_cover');
    const hasCover = typeCover === true ||
        (typeof typeCover === 'string' && typeCover.length > 0) ||
        matrixCover === true ||
        (typeof matrixCover === 'string' && matrixCover.length > 0);
    // Sumário: só é legítimo se o TIPO declarar que tem. Hoje nenhum declara —
    // então um sumário no documento é desvio (o modelo criou algo que o template
    // não prevê), e não uma região a ignorar.
    const hasToc = pick(type, 'document_structure', 'has_table_of_contents') === true ||
        pick(matrixEntry, 'has_table_of_contents') === true;
    return {
        headings: {
            Heading1: heading(1),
            Heading2: heading(2),
            Heading3: heading(3),
            Heading4: heading(4),
        },
        bodyFont: asString(pick(bodyRef, 'font_family'), 'Muli'),
        bodySize: asNumber(pick(bodyRef, 'font_size_pt'), 11),
        bodyLineSpacing: numOrNull(pick(bodyRef, 'line_spacing_pt')),
        bodySpaceBefore: numOrNull(pick(bodyRef, 'space_before_pt')),
        bodySpaceAfter: numOrNull(pick(bodyRef, 'space_after_pt')),
        bodyColors: new Set(bodyAllowed.map((c) => c.toUpperCase())),
        forbiddenColors: new Set(forbidden.map((c) => c.toUpperCase())),
        tableSize: asNumber(pick(base, 'table_contract', 'font_size_pt'), 10),
        hasCover,
        hasToc,
        emptyAfterTable: asNumber(pick(base, 'table_contract', 'empty_paragraphs_after_table'), 1),
        listSemicolon: pick(base, 'list_contract', 'intermediate_items_end_with_semicolon') === true,
        listLastPeriod: pick(base, 'list_contract', 'last_item_ends_with_period') === true,
    };
}
/** Valor concreto de fonte, ou null quando o Office reporta mistura (""/vazio). */
function concreteFont(name) {
    const v = (name !== null && name !== void 0 ? name : '').trim();
    return v ? v : null;
}
function concreteSize(size) {
    return typeof size === 'number' && size > 0 ? size : null;
}
function concreteColor(color) {
    // Só um hex real é "verdade". Vazio (misto) e "Automatic" (preto padrão do
    // Word) viram null — não afirmamos nada sobre eles (evita falso positivo).
    const hex = (color !== null && color !== void 0 ? color : '').trim().toUpperCase().replace(/^#/, '');
    return /^[0-9A-F]{6}$/.test(hex) ? `#${hex}` : null;
}
async function collectDocument() {
    return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items/text,items/style,items/styleBuiltIn,items/alignment,items/tableNestingLevel,' +
            'items/lineSpacing,items/spaceBefore,items/spaceAfter,' +
            'items/font/name,items/font/size,items/font/color,items/font/bold');
        const pictures = context.document.body.inlinePictures;
        pictures.load('items');
        await context.sync();
        const truncated = paragraphs.items.length > MAX_PARAGRAPHS;
        const items = paragraphs.items.slice(0, MAX_PARAGRAPHS);
        const paraData = items.map((p, index) => {
            var _a, _b, _c, _d, _e;
            return ({
                index,
                text: (_a = p.text) !== null && _a !== void 0 ? _a : '',
                style: String((_b = p.style) !== null && _b !== void 0 ? _b : ''),
                styleBuiltIn: String((_c = p.styleBuiltIn) !== null && _c !== void 0 ? _c : 'Other'),
                alignment: String((_d = p.alignment) !== null && _d !== void 0 ? _d : ''),
                fontName: concreteFont(p.font.name),
                fontSize: concreteSize(p.font.size),
                fontColor: concreteColor(p.font.color),
                fontBold: typeof p.font.bold === 'boolean' ? p.font.bold : null,
                lineSpacing: concreteSize(p.lineSpacing),
                spaceBefore: typeof p.spaceBefore === 'number' ? p.spaceBefore : null,
                spaceAfter: typeof p.spaceAfter === 'number' ? p.spaceAfter : null,
                inTable: ((_e = p.tableNestingLevel) !== null && _e !== void 0 ? _e : 0) > 0,
                runs: null,
            });
        });
        // 2º passo: parágrafos MISTOS (com texto real) — quebra em runs/palavras
        // para achar em QUAL trecho fonte/tamanho/cor divergem. Só entra aqui quem
        // não é uniforme; documento limpo não paga esse custo.
        // Gatilho inclui COR: null de cor no parágrafo é ambíguo (pode ser mistura
        // real OU "Automatic" uniforme do preto padrão), então o drill resolve —
        // nos runs, um trecho com cor concreta fora do padrão flagra; um doc todo
        // "Automatic" só produz runs null e nada é afirmado (sem falso positivo).
        // Sem a cor no gatilho, pintar UMA palavra do título de vermelho passava
        // batido: sem drill não há valor concreto para comparar (bug relatado).
        const mixedIdx = [];
        for (const p of paraData) {
            if (!p.text.trim())
                continue;
            const mixed = p.fontName === null || p.fontSize === null || p.fontColor === null;
            if (mixed)
                mixedIdx.push(p.index);
            if (mixedIdx.length >= MAX_MIXED_DRILL)
                break;
        }
        // ⚠️ Fase ISOLADA em try/catch: getTextRanges pode lançar em parágrafos
        // exóticos (só âncoras de desenho, ranges sem o separador...). Como um
        // Word.run rejeita a requisição INTEIRA se qualquer chamada enfileirada
        // falhar no sync, uma exceção aqui matava a validação toda em silêncio —
        // badge congelado no estado anterior e NADA era detectado. Degradar sem
        // runs mantém as checagens no nível do parágrafo funcionando.
        if (mixedIdx.length) {
            try {
                // Usa o CONTEÚDO (sem o ¶) para não confundir a fonte do fim de
                // parágrafo com o texto visível. `split` (não getTextRanges!): o
                // getTextRanges só devolve trechos TERMINADOS pelo separador — a
                // última palavra do parágrafo (sem espaço no fim) ficava fora, e era
                // exatamente ela que o usuário alterava sem detecção. split cobre
                // todos os pedaços, inclusive após o último separador.
                const rangeColls = mixedIdx.map((i) => items[i].getRange(Word.RangeLocation.content).split([' '], false, true, true));
                rangeColls.forEach((rc) => rc.load('items/text,items/font/name,items/font/size,items/font/color'));
                await context.sync();
                mixedIdx.forEach((paraIndex, k) => {
                    const runs = rangeColls[k].items.slice(0, MAX_RUNS_PER_PARAGRAPH).map((r) => {
                        var _a;
                        return ({
                            text: (_a = r.text) !== null && _a !== void 0 ? _a : '',
                            fontName: concreteFont(r.font.name),
                            fontSize: concreteSize(r.font.size),
                            fontColor: concreteColor(r.font.color),
                        });
                    });
                    paraData[paraIndex].runs = runs;
                });
            }
            catch (e) {
                console.warn('[collectDocument] drill de runs falhou (segue sem runs):', e);
            }
        }
        // Imagens: também isolado — falha aqui não pode derrubar o resto.
        let imagesBase64 = [];
        try {
            const picResults = pictures.items
                .slice(0, MAX_IMAGES)
                .map((pic) => pic.getBase64ImageSrc());
            if (picResults.length)
                await context.sync();
            imagesBase64 = picResults
                .map((r) => (r.value || '').replace(/^data:[^,]*,/, ''))
                .filter((b64) => b64.length > 0);
        }
        catch (e) {
            console.warn('[collectDocument] leitura de imagens falhou (segue sem):', e);
        }
        return { paragraphs: paraData, imagesBase64, truncated };
    });
}
// ---------------------------------------------------------------------------
// Checagens
// ---------------------------------------------------------------------------
/** Normaliza título para comparação: minúsculo, sem acento/numeração/':'. */
function normalizeTitle(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // remove os diacríticos separados pelo NFD
        .toLowerCase()
        .replace(/^\s*\d+(?:\.\d+)*\.?\s*/, '')
        .replace(/[:：]\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function snippet(text, max = 48) {
    const clean = text.replace(/\s+/g, ' ').trim();
    return clean.length <= max ? clean : `${clean.slice(0, max)}…`;
}
function normalizeFontName(fontName) {
    return (fontName || '')
        .toLowerCase()
        .replace(/["']/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function normalizeHexColor(fontColor) {
    const color = (fontColor || '').trim().toUpperCase();
    if (/^[0-9A-F]{6}$/.test(color))
        return `#${color}`;
    return color;
}
function hexToRgb(color) {
    const normalized = normalizeHexColor(color);
    const match = normalized.match(/^#([0-9A-F]{6})$/);
    if (!match)
        return null;
    const value = match[1];
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}
// ---------------------------------------------------------------------------
// Zona da capa: fronteira + checagem LENIENTE de marca
// ---------------------------------------------------------------------------
// A capa é diagramada pelo template.docx (região protegida) — não dá para
// exigir os valores de corpo lá (o título da capa é Styrene 24pt roxo de
// propósito). Mas pular a zona CEGAMENTE também erra: Arial vermelho na capa é
// violação óbvia. Meio-termo: na zona da capa checamos só se fonte e cor são
// DA MARCA (qualquer variante), sem julgar tamanho — pega o corpo estranho sem
// falso positivo na diagramação legítima.
/** Fontes da marca (norven_template.json: fonts.allowed_primary_families). */
const BRAND_FONT_PREFIXES = ['styrene a', 'muli', 'courier new'];
function isBrandFont(fontName) {
    const f = normalizeFontName(fontName);
    return BRAND_FONT_PREFIXES.some((b) => f === b || f.startsWith(`${b} `));
}
/** Cinza escuro/neutro (texto e variações do template, ex.: #313131). */
function isDarkNeutralColor(hex) {
    const color = normalizeHexColor(hex);
    if (color === '#1B1B1B' || color === '#000000' || color === '#3C3C3C')
        return true;
    const rgb = hexToRgb(color);
    if (!rgb)
        return false;
    const values = [rgb.r, rgb.g, rgb.b];
    const max = Math.max(...values);
    const min = Math.min(...values);
    return max <= 90 && max - min <= 12;
}
/** Paleta completa da marca (títulos, destaques, gráficos) + branco. */
const BRAND_COLORS = new Set([
    '#550899', '#AC4DFF', '#1B1B1B', '#000000', '#FFFFFF',
    '#518E16', '#AAF766', '#A34200', '#FF6A00', '#545454', '#3C3C3C',
]);
function isBrandColor(hex) {
    const color = normalizeHexColor(hex);
    return BRAND_COLORS.has(color) || isDarkNeutralColor(color);
}
// Cache do índice da 1ª fronteira de corpo por snapshot: isInCoverPrefix é
// chamado por parágrafo e recalcular a varredura toda vez seria O(n²).
const boundaryCache = new WeakMap();
/**
 * Onde a capa termina e o corpo começa. Sinais, do mais ao menos confiável:
 * quebra de página explícita no texto (\f — os templates Norven fecham a capa
 * com page break), campo TOC/rótulo "Sumário", ou o PRIMEIRO heading (numerado
 * OU NÃO — exigir numeração fazia docs com títulos sem número caírem no
 * fallback). Fallback apertado (12): o fallback antigo de 40 parágrafos fazia
 * documentos de teste inteiros serem tratados como "capa" e NADA era validado
 * (título alterado, Arial 28 no corpo — tudo passava; bug relatado).
 */
function firstBodyBoundaryIndex(snap) {
    const cached = boundaryCache.get(snap);
    if (cached !== undefined)
        return cached;
    let boundary = -1;
    for (const p of snap.paragraphs) {
        if (p.text.includes('\f')) {
            boundary = p.index + 1; // a quebra fecha a capa; o corpo começa depois
            break;
        }
        const text = p.text.trim();
        if (!text)
            continue;
        if (/^Toc\d|^TocHeading$/.test(p.styleBuiltIn) || normalizeTitle(text) === 'sumario') {
            boundary = p.index;
            break;
        }
        if (/^Heading[1-4]$/.test(p.styleBuiltIn)) {
            boundary = p.index;
            break;
        }
    }
    if (boundary < 0)
        boundary = Math.min(snap.paragraphs.length, 12);
    boundaryCache.set(snap, boundary);
    return boundary;
}
function isInCoverPrefix(p, snap) {
    return p.index < firstBodyBoundaryIndex(snap);
}
/** Um rótulo avulso "Sumário" (fora do campo TOC de verdade) não é corpo. */
function isStandaloneSumarioLabel(text) {
    return normalizeTitle(text) === 'sumario';
}
/** Segmentos de formatação: N runs se o parágrafo é misto, senão 1 (o todo). */
function segmentsOf(p) {
    if (p.runs && p.runs.length) {
        const named = p.runs.filter((r) => r.text.trim().length > 0);
        if (named.length)
            return named;
    }
    return [{ text: p.text, fontName: p.fontName, fontSize: p.fontSize, fontColor: p.fontColor }];
}
/**
 * Analisa UMA dimensão (fonte/tamanho/cor) sobre os segmentos: coleta os
 * valores concretos que violam a regra (distintos) e sinaliza mistura interna.
 */
function analyzeDim(segments, read, isBad, label) {
    const seen = new Set();
    const badLabels = [];
    let badSnippet = null;
    let mixed = false;
    for (const s of segments) {
        const v = read(s);
        if (v === null) {
            if (s.text.trim())
                mixed = true; // segmento não-uniforme = mistura real
            continue;
        }
        if (isBad(v)) {
            const lbl = label(v);
            if (!seen.has(lbl)) {
                seen.add(lbl);
                badLabels.push(lbl);
                if (badSnippet === null)
                    badSnippet = snippet(s.text, 30);
            }
        }
    }
    return { badLabels, badSnippet, mixed };
}
/** Heading? Por styleBuiltIn (Heading1-4) ou pelo nome localizado ("Título 2"). */
function headingSpecOf(p, c) {
    if (p.styleBuiltIn in c.headings)
        return c.headings[p.styleBuiltIn];
    const m = /(?:t[íi]tulo|heading)\s*([1-4])/i.exec(p.style || '');
    return m ? c.headings[`Heading${m[1]}`] : null;
}
/** Nível do heading (1–4) a partir do styleBuiltIn/nome localizado. */
function headingLevelOf(p) {
    const m1 = /^Heading([1-4])$/.exec(p.styleBuiltIn);
    if (m1)
        return Number(m1[1]);
    const m2 = /(?:t[íi]tulo|heading)\s*([1-4])/i.exec(p.style || '');
    return m2 ? Number(m2[1]) : null;
}
/**
 * Compara uma MÉTRICA de parágrafo (espaçamento, em pontos) com a norma.
 *
 * Só reporta quando os DOIS lados são concretos: a norma fixou o valor e o host
 * reportou o do documento. `null` de qualquer lado significa "não sei", e o
 * validador não afirma nada sobre o que não sabe — é o mesmo princípio
 * anti-falso-positivo do resto do módulo.
 *
 * Tolerância de 0,5pt absorve arredondamento do Word (14,4pt costuma voltar
 * como 14,4 mas já apareceu 14,39) sem deixar passar diferença real.
 */
const METRIC_TOLERANCE_PT = 0.5;
function metricViolation(atual, esperado, rotulo, onde) {
    if (atual === null || esperado === null)
        return null;
    if (Math.abs(atual - esperado) <= METRIC_TOLERANCE_PT)
        return null;
    return `${rotulo} de ${atual}pt (o padrão é ${esperado}pt) — “${onde}”.`;
}
function checkParagraphs(snap, c) {
    const out = [];
    let currentSection = null;
    // Estado das checagens ESTRUTURAIS (determinísticas, não opinativas):
    // último nível de título visto e se já reportamos o sumário indevido.
    let lastHeadingLevel = 0;
    let tocReported = false;
    snap.paragraphs.forEach((p) => {
        var _a, _b, _c, _d, _e;
        const text = p.text.trim();
        if (!text)
            return; // vazio: nada a validar
        const headingSpec = headingSpecOf(p, c);
        const isToc = /^Toc\d|^TocHeading$/.test(p.styleBuiltIn);
        const isQuote = p.styleBuiltIn === 'Quote' || p.styleBuiltIn === 'IntenseQuote';
        const isList = p.styleBuiltIn === 'ListParagraph';
        const isBodyStyle = p.styleBuiltIn === 'Normal' || p.styleBuiltIn === 'NoSpacing';
        const segs = segmentsOf(p);
        const fontLabel = (v) => `"${v}"`;
        const sizeLabel = (v) => `${v}pt`;
        const colorLabel = (v) => normalizeHexColor(String(v));
        // ---------- ZONA DA CAPA (leniente, nunca cega) ----------
        // A diagramação da capa é do template.docx (região protegida) — não
        // julgamos tamanho nem exigimos os valores de corpo (o título da capa é
        // Styrene 24pt roxo de propósito; validar contra "Muli 11" gerava falso
        // positivo). Mas TAMBÉM não pulamos cegamente: fonte fora da MARCA ou cor
        // fora da PALETA na capa é violação real (ex.: Arial vermelho). Headings
        // de verdade nunca caem aqui — são validados por completo mais abaixo,
        // estejam onde estiverem (pular headings foi o que escondeu título
        // alterado do usuário; bug relatado).
        if (c.hasCover && isInCoverPrefix(p, snap) && !headingSpec) {
            const fa = analyzeDim(segs, (s) => s.fontName, (v) => !isBrandFont(String(v)), fontLabel);
            if (fa.badLabels.length) {
                out.push({
                    rule: 'cover.font',
                    section: 'Capa',
                    message: `Trecho da capa com fonte ${fa.badLabels.join(', ')} — a capa usa apenas fontes da marca (Styrene A / Muli).`,
                });
            }
            const ca = analyzeDim(segs, (s) => s.fontColor, (v) => !isBrandColor(String(v)), colorLabel);
            if (ca.badLabels.length) {
                out.push({
                    rule: 'cover.color',
                    section: 'Capa',
                    message: `Trecho da capa na cor ${ca.badLabels.join(', ')}, fora da paleta Norven.`,
                });
            }
            return;
        }
        if (isStandaloneSumarioLabel(text))
            return; // "Sumário" solto fora do campo TOC
        // ---------- TÍTULOS ----------
        if (headingSpec) {
            const spec = headingSpec;
            currentSection = snippet(text, 60);
            // Estrutura: pular nível (H1 → H3 sem H2) é proibido ao criar conteúdo.
            // Só comparamos entre títulos CONSECUTIVOS (lastHeadingLevel > 0) — assim
            // um documento que começa num subtítulo não é acusado à toa.
            const level = headingLevelOf(p);
            if (level !== null) {
                if (lastHeadingLevel > 0 && level > lastHeadingLevel + 1) {
                    out.push({
                        rule: 'structure.heading_level_skip',
                        section: currentSection,
                        message: `O título «${snippet(text)}» é de nível ${level} logo abaixo de um nível ${lastHeadingLevel} — não pule níveis (falta o nível ${lastHeadingLevel + 1}).`,
                    });
                }
                lastHeadingLevel = level;
            }
            // Estrutura: o respiro vem do estilo, não de linha em branco. Só flagra
            // quando a linha vazia está ENTRE conteúdos — um parágrafo vazio no fim
            // do documento é do template, não um erro de escrita.
            const next = snap.paragraphs[p.index + 1];
            const hasContentAfter = snap.paragraphs
                .slice(p.index + 2)
                .some((q) => q.text.trim().length > 0);
            if (next && !next.text.trim() && !next.inTable && hasContentAfter) {
                out.push({
                    rule: 'structure.empty_after_heading',
                    section: currentSection,
                    message: `Há um parágrafo vazio depois do título «${snippet(text)}» — o espaçamento vem do estilo; remova a linha em branco.`,
                });
            }
            if (!spec.pattern.test(text)) {
                out.push({
                    rule: 'heading.numbering',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» está sem numeração no padrão (ex.: "${spec.label === 'tópico principal' ? '1. Título' : '1.1 Subtítulo'}").`,
                });
            }
            const normSpecFont = normalizeFontName(spec.font);
            const fa = analyzeDim(segs, (s) => s.fontName, (v) => normalizeFontName(String(v)) !== normSpecFont, fontLabel);
            if (fa.badLabels.length) {
                out.push({
                    rule: 'heading.font',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» tem trecho na fonte ${fa.badLabels.join(', ')} — o padrão é "${spec.font}".`,
                });
            }
            else if (fa.mixed) {
                out.push({
                    rule: 'heading.font',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» mistura fontes no mesmo título — use só "${spec.font}".`,
                });
            }
            const za = analyzeDim(segs, (s) => s.fontSize, (v) => Math.abs(Number(v) - spec.size) > 0.25, sizeLabel);
            if (za.badLabels.length) {
                out.push({
                    rule: 'heading.size',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» tem trecho com ${za.badLabels.join(', ')} — o padrão é ${spec.size}pt.`,
                });
            }
            else if (za.mixed) {
                out.push({
                    rule: 'heading.size',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» mistura tamanhos de fonte — use ${spec.size}pt.`,
                });
            }
            // Cor: SÓ valores concretos flagram. `null` de cor é ambíguo (mistura OU
            // "Automatic" uniforme) — afirmar "mistura" por null gerava falso
            // positivo em texto preto padrão. O drill (cor no gatilho) garante que
            // mistura real produza runs com valores concretos para comparar.
            const ca = analyzeDim(segs, (s) => s.fontColor, (v) => normalizeHexColor(String(v)) !== spec.color, colorLabel);
            if (ca.badLabels.length) {
                out.push({
                    rule: 'heading.color',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» tem trecho na cor ${ca.badLabels.join(', ')} — o padrão deste nível é ${spec.colorName} (${spec.color}).`,
                });
            }
            // Negrito: a hierarquia distingue os níveis também por peso — heading_4 é
            // o único sem negrito, e trocar isso descaracteriza o nível.
            if (spec.bold !== null && p.fontBold !== null && p.fontBold !== spec.bold) {
                out.push({
                    rule: 'heading.bold',
                    section: currentSection,
                    message: `O ${spec.label} «${snippet(text)}» está ${p.fontBold ? 'em negrito' : 'sem negrito'} — o padrão deste nível é ${spec.bold ? 'negrito' : 'sem negrito'}.`,
                });
            }
            // Respiro do título: vem do estilo, e é o que separa os blocos na página.
            for (const [atual, esperado, rotulo] of [
                [p.spaceBefore, spec.spaceBefore, 'Espaço ANTES'],
                [p.spaceAfter, spec.spaceAfter, 'Espaço DEPOIS'],
            ]) {
                const msg = metricViolation(atual, esperado, rotulo, snippet(text));
                if (msg) {
                    out.push({ rule: 'heading.spacing', section: currentSection, message: msg });
                }
            }
            return;
        }
        // ---------- SUMÁRIO ----------
        // Formatação do sumário é do Word (não validamos fonte). Mas se o TIPO não
        // prevê sumário — hoje, nenhum —, a existência dele é desvio: alguém criou
        // uma região que o template não tem. Reportado UMA vez (um sumário tem
        // dezenas de parágrafos).
        if (isToc) {
            if (!c.hasToc && !tocReported) {
                tocReported = true;
                out.push({
                    rule: 'structure.toc_not_allowed',
                    section: 'Sumário',
                    message: 'O documento tem sumário, e este tipo não prevê sumário — a navegação é a numeração dos tópicos no corpo. Remova o índice.',
                });
            }
            return;
        }
        if (isQuote)
            return; // citações têm estilo próprio
        // ---------- TABELAS ----------
        if (p.inTable) {
            // Muli 10 (contrato de tabela); 11 tolerado (célula editada como corpo).
            const za = analyzeDim(segs, (s) => s.fontSize, (v) => Math.abs(Number(v) - c.tableSize) > 0.25 && Math.abs(Number(v) - c.bodySize) > 0.25, sizeLabel);
            if (za.badLabels.length) {
                out.push({
                    rule: 'table.size',
                    section: currentSection,
                    message: `Texto de tabela com ${za.badLabels.join(', ')} — o padrão é ${c.tableSize}pt — “${snippet(text)}”.`,
                });
            }
            return;
        }
        // ---------- CORPO CLÁSSICO (regras estritas) ----------
        if (isBodyStyle || isList) {
            // Marcador digitado à mão: o padrão Norven é a barra "/" aplicada por
            // insert_list. Hífen/asterisco/bullet digitados são desvio. Exige espaço
            // depois do sinal — "-15%" ou "*nota" no meio do texto não é marcador.
            if (/^\s*[-*•·]\s+\S/.test(p.text)) {
                out.push({
                    rule: 'list.typed_bullet',
                    section: currentSection,
                    message: `Item começa com marcador digitado à mão — use insert_list (o marcador da marca é "/") — “${snippet(text)}”.`,
                });
            }
            // Barra da marca em parágrafo de CORPO: o conteúdo é lista, mas o estilo
            // não é o de lista (recuo e marcador saem errados).
            if (isBodyStyle && /^\s*\/[ \t]+\S/.test(p.text)) {
                out.push({
                    rule: 'list.style_mismatch',
                    section: currentSection,
                    message: `Parágrafo de corpo começa com a barra de lista — aplique slash_bullet_list — “${snippet(text)}”.`,
                });
            }
            const normBodyFont = normalizeFontName(c.bodyFont);
            const fa = analyzeDim(segs, (s) => s.fontName, (v) => normalizeFontName(String(v)) !== normBodyFont, fontLabel);
            if (fa.badLabels.length) {
                out.push({
                    rule: 'body.font',
                    section: currentSection,
                    message: `Trecho na fonte ${fa.badLabels.join(', ')} (o padrão do corpo é "${c.bodyFont}") — “${(_a = fa.badSnippet) !== null && _a !== void 0 ? _a : snippet(text)}”.`,
                });
            }
            else if (fa.mixed) {
                out.push({
                    rule: 'body.font',
                    section: currentSection,
                    message: `Parágrafo mistura fontes (padrão: "${c.bodyFont}") — “${snippet(text)}”.`,
                });
            }
            const za = analyzeDim(segs, (s) => s.fontSize, (v) => Math.abs(Number(v) - c.bodySize) > 0.25, sizeLabel);
            if (za.badLabels.length) {
                out.push({
                    rule: 'body.size',
                    section: currentSection,
                    message: `Trecho com ${za.badLabels.join(', ')} (padrão do corpo: ${c.bodySize}pt) — “${(_b = za.badSnippet) !== null && _b !== void 0 ? _b : snippet(text)}”.`,
                });
            }
            else if (za.mixed) {
                out.push({
                    rule: 'body.size',
                    section: currentSection,
                    message: `Parágrafo mistura tamanhos de fonte (padrão: ${c.bodySize}pt) — “${snippet(text)}”.`,
                });
            }
            // Cor: SÓ valores concretos flagram (null = ambíguo com "Automatic";
            // afirmar "mistura" por null gerava falso positivo em preto padrão — o
            // drill com cor no gatilho garante runs concretos quando há mistura real).
            const ca = analyzeDim(segs, (s) => s.fontColor, (v) => !c.bodyColors.has(normalizeHexColor(String(v))), colorLabel);
            if (ca.badLabels.length) {
                const forbidden = ca.badLabels.filter((h) => c.forbiddenColors.has(h));
                const note = forbidden.length ? ' (proibida para corpo)' : ', fora das cores de corpo permitidas';
                out.push({
                    rule: forbidden.length ? 'body.forbidden_color' : 'body.color',
                    section: currentSection,
                    message: `Trecho na cor ${ca.badLabels.join(', ')}${note} — “${(_c = ca.badSnippet) !== null && _c !== void 0 ? _c : snippet(text)}”.`,
                });
            }
            // Métricas de espaçamento do corpo: entrelinha e respiro entre parágrafos.
            // São propriedade do PARÁGRAFO (não do run) e é o que mais denuncia texto
            // colado de fora — o Word traz o espaçamento da origem junto.
            for (const [atual, esperado, rotulo] of [
                [p.lineSpacing, c.bodyLineSpacing, 'Entrelinha'],
                [p.spaceBefore, c.bodySpaceBefore, 'Espaço ANTES do parágrafo'],
                [p.spaceAfter, c.bodySpaceAfter, 'Espaço DEPOIS do parágrafo'],
            ]) {
                const msg = metricViolation(atual, esperado, rotulo, snippet(text));
                if (msg) {
                    out.push({ rule: 'body.spacing', section: currentSection, message: msg });
                }
            }
            // Alinhamento é propriedade do parágrafo (não do run).
            if (isBodyStyle && p.alignment && p.alignment !== 'Justified' && p.alignment !== 'Mixed') {
                out.push({
                    rule: 'body.alignment',
                    section: currentSection,
                    message: `Parágrafo alinhado como "${p.alignment}" (o corpo Norven é justificado) — “${snippet(text)}”.`,
                });
            }
            return;
        }
        // ---------- DEMAIS ESTILOS (branch padrão — leniente de marca) ----------
        // Um parágrafo em qualquer OUTRO estilo (Subtitle, Title, Caption, custom,
        // "Other") também é conteúdo e precisa seguir a marca. Antes, esses estilos
        // não recebiam NENHUMA checagem — texto Arial vermelho 22pt digitado num
        // parágrafo de estilo "exótico" passava batido (bug relatado). A régua aqui
        // é a LENIENTE (fonte da marca + cor da paleta, sem tamanho): exigir Muli
        // estrita acusaria elementos legítimos do template (Subtítulo em Styrene,
        // cinza #313131), e o objetivo é pegar o que é claramente estranho.
        {
            const fa = analyzeDim(segs, (s) => s.fontName, (v) => !isBrandFont(String(v)), fontLabel);
            if (fa.badLabels.length) {
                out.push({
                    rule: 'body.font',
                    section: currentSection,
                    message: `Trecho na fonte ${fa.badLabels.join(', ')}, fora das fontes da marca (Styrene A / Muli) — “${(_d = fa.badSnippet) !== null && _d !== void 0 ? _d : snippet(text)}”.`,
                });
            }
            const ca = analyzeDim(segs, (s) => s.fontColor, (v) => !isBrandColor(String(v)), colorLabel);
            if (ca.badLabels.length) {
                out.push({
                    rule: 'body.color',
                    section: currentSection,
                    message: `Trecho na cor ${ca.badLabels.join(', ')}, fora da paleta Norven — “${(_e = ca.badSnippet) !== null && _e !== void 0 ? _e : snippet(text)}”.`,
                });
            }
        }
    });
    return out;
}
// ---------------------------------------------------------------------------
// Checagens de ESTRUTURA (documento inteiro, não parágrafo a parágrafo)
// ---------------------------------------------------------------------------
// `checkParagraphs` é um linter de FORMATAÇÃO: olha um parágrafo por vez.
// `checkStructure` olha o documento inteiro — sequência da numeração, título
// vazio, linha em branco entre conteúdos, espaço depois de tabela e pontuação
// dos itens de lista. Os VALORES continuam vindo do rules.json
// (`numbering_contract`, `table_contract`, `list_contract`); nenhuma checagem
// aqui julga se falta uma seção ou se o texto está bem escrito — isso é do
// prompt e da revisão humana (ver o cabeçalho do arquivo).
function checkStructure(snap, c) {
    var _a;
    const out = [];
    const body = snap.paragraphs.filter((p) => !isInCoverPrefix(p, snap));
    if (!body.length)
        return out;
    const headingsComTexto = [];
    let secaoAtual = null;
    // ---------- Título VAZIO ----------
    // `checkParagraphs` ignora parágrafo sem texto (`if (!text) return`), então um
    // heading vazio nunca era visto. Ele rende uma linha em branco com espaçamento
    // de título e entraria num Sumário como item sem nome. Caso real: um
    // `Heading 1` vazio sobrando no fim de um documento gerado.
    for (const p of body) {
        const nivel = headingLevelOf(p);
        if (nivel === null)
            continue;
        if (p.text.trim())
            headingsComTexto.push(p);
        else if (!p.inTable) {
            out.push({
                rule: 'structure.empty_heading',
                section: secaoAtual,
                message: `Há um título de nível ${nivel} sem texto — remova a linha ou escreva o título.`,
            });
        }
        if (p.text.trim())
            secaoAtual = snippet(p.text, 60);
    }
    // ---------- Sequência da numeração ----------
    // `heading.numbering` já cobra o FORMATO ("1." / "1.1"). Aqui é a SEQUÊNCIA,
    // que `numbering_contract.rules` pede preservar: um documento que pula de
    // "1." para "5." tem furo de estrutura, e é exatamente o sintoma de escrever
    // um tópico do esqueleto sem os anteriores.
    const contadores = [];
    for (const p of headingsComTexto) {
        const m = /^\s*(\d+(?:\.\d+)*)/.exec(p.text.trim());
        const nivel = headingLevelOf(p);
        if (!m || nivel === null)
            continue;
        const partes = m[1].split('.').map(Number);
        // Só compara quando a profundidade do número bate com o nível do estilo —
        // "1.1" num Heading1 é outro defeito, já coberto por `heading.numbering`.
        if (partes.length !== nivel)
            continue;
        contadores.length = Math.max(contadores.length, nivel);
        const anterior = (_a = contadores[nivel - 1]) !== null && _a !== void 0 ? _a : 0;
        const atual = partes[nivel - 1];
        if (anterior > 0 && atual !== anterior + 1) {
            out.push({
                rule: 'structure.numbering_sequence',
                section: snippet(p.text, 60),
                message: `A numeração salta de ${anterior} para ${atual} em «${snippet(p.text)}» — ` +
                    'a sequência dos tópicos precisa ser contínua (crie os que faltam, nem que seja só o título).',
            });
        }
        contadores[nivel - 1] = atual;
        contadores.length = nivel; // trocar de tópico reinicia os subníveis
    }
    // ---------- Respiro: parágrafo vazio entre conteúdos ----------
    // `validation_checklist_before_final_answer` pede: "Não há parágrafo vazio
    // depois de tópico, subtópico, corpo ou item de lista". O caso do título já é
    // reportado em `checkParagraphs`; aqui ficam corpo e lista. A exceção é a
    // linha DEPOIS DE TABELA, que o `table_contract` exige (tratada abaixo).
    const temConteudoApos = (i) => body.slice(i + 1).some((q) => q.text.trim().length > 0);
    let vaziosReportados = 0;
    for (let i = 1; i < body.length; i++) {
        const p = body[i];
        if (p.text.trim() || p.inTable)
            continue;
        const anterior = body[i - 1];
        if (!anterior.text.trim())
            continue; // sequência de vazios: reporta uma vez
        if (anterior.inTable)
            continue; // o vazio depois de tabela é exigido
        if (headingLevelOf(anterior) !== null)
            continue; // já coberto
        if (!temConteudoApos(i))
            continue; // vazio final é do template
        if (vaziosReportados++ >= 3)
            break; // não inunda o badge
        out.push({
            rule: 'structure.empty_paragraph',
            section: snippet(anterior.text, 60),
            message: `Há uma linha em branco depois de «${snippet(anterior.text)}» — o espaçamento vem do estilo; remova o parágrafo vazio.`,
        });
    }
    // ---------- Respiro depois de TABELA ----------
    // `table_contract.empty_paragraphs_after_table` é um número: exatamente 1.
    for (let i = 0; i < body.length; i++) {
        if (!body[i].inTable)
            continue;
        if (i + 1 < body.length && body[i + 1].inTable)
            continue; // ainda dentro
        // Fim de tabela: conta os vazios até o próximo conteúdo.
        let vazios = 0;
        let j = i + 1;
        while (j < body.length && !body[j].text.trim() && !body[j].inTable) {
            vazios++;
            j++;
        }
        if (j >= body.length)
            break; // tabela no fim do documento: nada a exigir
        if (vazios !== c.emptyAfterTable) {
            out.push({
                rule: 'table.spacing',
                section: snippet(body[i].text, 60),
                message: vazios < c.emptyAfterTable
                    ? `A tabela está colada no bloco seguinte — o padrão pede ${c.emptyAfterTable} parágrafo vazio depois dela.`
                    : `Há ${vazios} parágrafos vazios depois da tabela — o padrão pede exatamente ${c.emptyAfterTable}.`,
            });
        }
        i = j - 1;
    }
    // ---------- Pontuação dos itens de lista ----------
    // `list_contract`: item intermediário fecha com ";", o último com ".". É a
    // ÚNICA situação em que a norma prefere o ponto e vírgula (ver
    // `semicolon_here_is_intentional`). Uma violação por LISTA, não por item.
    if (c.listSemicolon || c.listLastPeriod) {
        let i = 0;
        while (i < body.length) {
            if (body[i].styleBuiltIn !== 'ListParagraph' || !body[i].text.trim() || body[i].inTable) {
                i++;
                continue;
            }
            const inicio = i;
            while (i < body.length &&
                body[i].styleBuiltIn === 'ListParagraph' &&
                body[i].text.trim() &&
                !body[i].inTable) {
                i++;
            }
            const itens = body.slice(inicio, i);
            if (itens.length < 2)
                continue; // item solto não forma lista
            const fim = (t) => t.trim().slice(-1);
            const errados = itens.filter((it, k) => {
                const ultimo = k === itens.length - 1;
                if (ultimo)
                    return c.listLastPeriod && fim(it.text) !== '.';
                return c.listSemicolon && fim(it.text) !== ';';
            });
            if (errados.length) {
                out.push({
                    rule: 'list.punctuation',
                    section: snippet(itens[0].text, 60),
                    message: `${errados.length} de ${itens.length} itens da lista que começa em «${snippet(itens[0].text, 34)}» ` +
                        'não fecham como a norma pede (";" nos intermediários, "." no último).',
                });
            }
        }
    }
    return out;
}
/**
 * Seam de teste. `checkStructure` e `buildContracts` são lógica pura (não tocam
 * Office.js), então dá para exercitá-las contra um .docx real fora do Word — é
 * o que o harness de validação faz. Não use na UI: o caminho público é
 * `validateDocument`.
 * @internal
 */
export const __validatorInternals = { buildContracts, checkStructure };
async function checkImages(snap) {
    if (!snap.imagesBase64.length)
        return { violations: [], skipped: false, checked: 0 };
    // (Promise.allSettled exigiria lib es2020; este wrapper faz o mesmo.)
    const results = await Promise.all(snap.imagesBase64.map((b64) => validateImage(b64).then((value) => ({ ok: true, value }), () => ({ ok: false, value: null }))));
    const violations = [];
    let skipped = false;
    results.forEach((res, i) => {
        var _a;
        if (!res.ok || !res.value) {
            skipped = true; // backend fora/erro: não afirmamos nada sobre a imagem
            return;
        }
        const r = res.value;
        if (!r.compliant) {
            const colors = ((_a = r.offending_colors) !== null && _a !== void 0 ? _a : [])
                .slice(0, 3)
                .map((c) => { var _a; return `${c.hex} (~${Math.round(((_a = c.share_visible) !== null && _a !== void 0 ? _a : 0) * 100)}%)`; })
                .join(', ');
            violations.push({
                rule: 'image.palette',
                section: null,
                message: `A ${i + 1}ª imagem do documento tem cores fora da paleta corporativa` +
                    (colors ? `: ${colors}.` : '.'),
            });
        }
    });
    return { violations, skipped, checked: snap.imagesBase64.length };
}
// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------
/**
 * Ordem de exibição das violações. O balão do badge mostra 6 e o bloco de
 * "Conformidade" que vai ao modelo mostra poucos itens — sem prioridade, três
 * linhas em branco empurrariam "faltam as seções Cronograma, Equipe e
 * Governança" para fora da lista. Menor número = aparece primeiro.
 *
 * A régua é o custo de deixar passar: uma seção que falta reprova a proposta na
 * revisão; uma linha em branco a mais, não.
 */
const RULE_PRIORITY = {
    // Fonte/cor erradas são o que mais salta aos olhos num documento impresso.
    'body.forbidden_color': 0,
    'heading.font': 1,
    'heading.size': 1,
    'heading.color': 1,
    'body.font': 1,
    'body.size': 1,
    'body.color': 1,
    'heading.bold': 2,
    'body.alignment': 2,
    'cover.font': 2,
    'cover.color': 2,
    // Estrutura mecânica: numeração e hierarquia dos títulos.
    'heading.numbering': 3,
    'structure.numbering_sequence': 3,
    'structure.heading_level_skip': 3,
    'structure.empty_heading': 3,
    'structure.toc_not_allowed': 3,
    // Espaçamento: entrelinha, respiro e linhas em branco.
    'heading.spacing': 4,
    'body.spacing': 4,
    'structure.empty_after_heading': 5,
    'structure.empty_paragraph': 5,
    'table.spacing': 5,
    // Listas, tabelas e imagens.
    'list.typed_bullet': 6,
    'list.style_mismatch': 6,
    'list.punctuation': 6,
    'table.size': 6,
    'image.palette': 7,
};
/** Estável: mantém a ordem do documento dentro de uma mesma prioridade. */
function sortViolations(violations) {
    return violations
        .map((v, i) => { var _a; return ({ v, i, p: (_a = RULE_PRIORITY[v.rule]) !== null && _a !== void 0 ? _a : 5 }); })
        .sort((a, b) => a.p - b.p || a.i - b.i)
        .map((x) => x.v);
}
/**
 * Varre o documento e devolve o relatório de conformidade com o padrão do
 * tipo selecionado. Nunca lança (erro ⇒ relatório vazio "ok" com log) — a
 * validação jamais pode quebrar o chat.
 */
export async function validateDocument(typeId) {
    try {
        const [base, type] = await Promise.all([readRulesJson('_base'), readRulesJson(typeId)]);
        const contracts = buildContracts(base, type);
        const snap = await collectDocument();
        // Duas passadas com escopos distintos: `checkParagraphs` olha um parágrafo
        // por vez (formatação); `checkStructure` olha o documento (numeração,
        // respiro, seções da norma do tipo). A ordem importa só para a leitura do
        // balão — formatação primeiro, estrutura depois.
        const violations = checkParagraphs(snap, contracts);
        violations.push(...checkStructure(snap, contracts));
        const images = await checkImages(snap);
        violations.push(...images.violations);
        const report = {
            ok: violations.length === 0,
            violations: sortViolations(violations),
            checkedParagraphs: snap.paragraphs.length,
            checkedImages: images.checked,
            imagesSkipped: images.skipped,
            truncated: snap.truncated,
            ranAt: Date.now(),
        };
        dlog('[validateDocument] relatório:', report);
        return report;
    }
    catch (e) {
        console.warn('[validateDocument] falhou (validação nunca quebra o chat):', e);
        return null;
    }
}
/** Texto do balão (tooltip) para o estado com problemas. Determinístico. */
export function formatProblems(report, max = 6) {
    const lines = report.violations.slice(0, max).map((v) => {
        const where = v.section ? ` [em: ${v.section}]` : '';
        return `• ${v.message}${where}`;
    });
    const rest = report.violations.length - max;
    // "desvio" é o vocabulário do sistema de design (o badge diz "N DESVIOS");
    // manter "problema" aqui deixaria o balão falando outra língua que o badge.
    if (rest > 0)
        lines.push(`…e mais ${rest} desvio${rest > 1 ? 's' : ''}.`);
    if (report.imagesSkipped) {
        lines.push('(As imagens não puderam ser verificadas nesta rodada.)');
    }
    return lines.join('\n');
}
